import {
  BackgroundColors,
  NegativeColors,
  PositiveColors,
  TextColors,
} from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';

import { createUseStyles } from 'react-jss';

import { BreakPoints } from '@picsart/design-system/foundation/responsive';
import { Easing } from '@picsart/design-system/foundation/easing';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { Timings } from '@picsart/design-system/foundation/timings';
import { mediaQuery } from '@picsart/rc/helpers';

const useStyles = createUseStyles(
  () => ({
    root: {
      padding: [Spacings.s64, Spacings.s0],

      [mediaQuery(BreakPoints.Large)]: {
        padding: [Spacings.s40, Spacings.s0],
      },

      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        padding: [Spacings.s64, Spacings.s0],
      },

      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        padding: [Spacings.s40, Spacings.s0],
      },
    },
    titleHolder: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      margin: [Spacings.s0, 'auto', Spacings.s16],

      '&:before, &:after': {
        content: "''",
        backgroundColor: PositiveColors.base.default,
        maxWidth: 546,
        display: 'inline-block',
        width: '-webkit-fill-available',
        height: 2,

        // [mediaQuery(BreakPoints.Small)]: {
        '@media screen and (max-width: 766px)': {
          display: 'none',
        },
      },

      // [mediaQuery(766)]: {
      '@media screen and (max-width: 766px)': {
        margin: [Spacings.s0, 'auto', Spacings.s12],
      },
    },
    title: {
      display: '-webkit-box',
      minHeight: 32,
      minWidth: 400,
      margin: [Spacings.s0, 'auto'],
      lineClamp: 2,
      boxOrient: 'vertical',
      fontSize: FontSizes.s30,
      fontWeight: FontWeights.semibold,
      textAlign: 'center',
      lineHeight: LineHeights.l36,
      color: TextColors.tint1,
      overflow: 'hidden',

      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        '-webkit-line-clamp': 3,
      },

      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        '-webkit-line-clamp': 4,
        fontSize: Spacings.s24,
        lineHeight: LineHeights.l32,
        minWidth: 250,
      },
    },
    carousel: {
      marginBottom: Spacings.s44,

      '& > div[class^="wrapper"]': {
        overflow: 'visible',
      },

      '& $carouselContainer': {
        overflow: 'visible',
      },
    },
    carouselContainer: {
      '& > $carouselDots': {
        margin: [Spacings.s0, 'auto', Spacings.s32],
      },

      '& $control': {
        '&.prev': {
          left: `calc(${Spacings.s32} * -1)`,

          // [mediaQuery(BreakPoints.Small)]: {
          '@media screen and (max-width: 766px)': {
            left: Spacings.s0,
          },
        },

        '&.next': {
          right: `calc(${Spacings.s32} * -1)`,

          // [mediaQuery(BreakPoints.Small)]: {
          '@media screen and (max-width: 766px)': {
            right: Spacings.s0,
          },
        },

        '& > button': {
          backgroundColor: BackgroundColors.tint2,
          width: 28,
          height: 56,
        },

        // [mediaQuery(766)]: {
        '@media screen and (max-width: 766px)': {
          display: 'none',
        },
      },

      '& $dots': {
        margin: [Spacings.s0, 'auto', Spacings.s32],
      },
    },
    dots: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: [Spacings.s0, 'auto'],
      width: 48,

      '& > button': {
        height: 8,
        width: 8,
        backgroundColor: BackgroundColors.tint3,

        '&.active': {
          backgroundColor: NegativeColors.base.default,
        },
      },
    },
    control: {
      display: 'flex',
      alignItems: 'center',
      transition: `all ${Timings.default} ${Easing.easeInOut}`,

      '& > button': {
        width: 28,
        height: 56,
        borderRadius: CornerRadius.r8,
        backgroundColor: BackgroundColors.tint2,

        '& svg': {
          width: 16,
          height: 16,

          '& > path': {
            fill: NegativeColors.base.activeAction,
          },
        },
      },

      '&.disabled': {
        visibility: 'hidden',
        opacity: 0,

        '&.prev': {
          transform: 'translateX(-10px)',
        },

        '&.next': {
          transform: 'translateX(10px)',
        },
      },

      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        '&.next > button': {
          borderBottomRightRadius: CornerRadius.r0,
          borderTopRightRadius: CornerRadius.r0,
        },

        '&.prev > button': {
          borderBottomLeftRadius: CornerRadius.r0,
          borderTopLeftRadius: CornerRadius.r0,
        },
      },
    },
    action: {
      margin: [Spacings.s0, 'auto'],
      padding: [Spacings.s16, Spacings.s32],
      minWidth: 200,
      width: 'fit-content',
      maxWidth: 456,
      fontSize: FontSizes.s16,
      fontWeight: FontWeights.medium,
      lineHeight: LineHeights.l24,

      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        width: '100%',
      },
    },
    layout: {
      margin: [Spacings.s0, 'auto'],
      width: '100%',
      maxWidth: 1076,
      minHeight: 376,

      [mediaQuery(BreakPoints.Large)]: {
        position: 'initial',
        maxWidth: 934,
        minHeight: 344,
      },

      // // [mediaQuery(BreakPoints.Small)]: {
      // '@media screen and (max-width: 766px)': {
      //   minHeight: 426,
      // },

      // [mediaQuery(766)]: {
      '@media screen and (max-width: 766px)': {
        padding: [Spacings.s0, Spacings.s24],
        minHeight: 376,
      },
    },
    // skeleton
    skeletonRoot: {
      margin: [Spacings.s0, 'auto'],
      width: '100%',
      maxWidth: 1320,
    },
    skeletonTitle: {
      margin: [Spacings.s0, 'auto'],
      minHeight: 32,
      maxWidth: 400,
    },

    sekeletonBtn: {
      margin: [Spacings.s8, 'auto'],
      height: 56,
      width: 200,
    },
    sekeletonItemWrapper: {
      display: 'flex',
      justifyContent: 'space-around',
      width: '100%',

      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        flexDirection: 'column',
      },
    },
    skeletonItem: {
      margin: [Spacings.s24, 'auto', Spacings.s24],
      width: 342,
      height: 234,

      // [mediaQuery(BreakPoints.Small)]: {
      '@media screen and (max-width: 766px)': {
        flexDirection: 'column',
        width: 72,
        height: 72,
      },
    },
    skeletonDots: {
      display: 'flex',
      margin: [Spacings.s24, 'auto', Spacings.s24],
      justifyContent: 'space-around',
      width: 48,
    },
    skeletonDotsItem: {
      borderRadius: CornerRadius.circle,
      width: 8,
      height: 8,
    },
  }),
  {
    name: 'blog-carousel',
  },
);

export default useStyles;
